import AddIcon from '@mui/icons-material/Add';
import { Tooltip } from '@mui/material';
import classNames from 'classnames';
import React, { useState, useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { getAuthPersonSelector } from '@redux/oauth/oauthSelectors';
import { getActiveOrganizationIdSelector, isDTCOrganizationCaregiverSelector } from '@redux/organization/organizationSelectors';
import { clearUpdatingEngagementPlanAction } from '@redux/tasks/tasksActions';
import { getUpdatingEngagementPlanSelector } from '@redux/tasks/tasksSelectors';
import { cancelEngagementPlanThunk } from '@redux/tasks/tasksThunks';
import { PatientEngagementPlan } from '@redux/tasks/tasksTypes';
import { ReduxState } from '@redux/types';
import { Button } from 'components/Button/Button';
import { ConfirmationDialogV2 } from 'components/ConfirmationDialog/ConfirmationDialogV2';
import { EmptyListArea } from 'components/EmptyListArea/EmptyListArea';
import { EngagementPlanCard } from 'components/EngagementPlanCard';
import EngagementPlanDetailsDrawer from 'components/EngagementPlanDetailsDrawer';
import EngagementPlanSelectionDrawer from 'components/EngagementPlanSelectionDrawer/EngagementPlanSelectionDrawer';
import EngagementPlanUpdateDrawer from 'components/EngagementPlanUpdateDrawer/EngagementPlanUpdateDrawer';
import { CopyIcon } from 'components/Icons';
import { WarningCircleIcon } from 'components/Icons/WarningCircleIcon';
import { Text } from 'components/Typography';
import { parseDateStringIgnoreTimezone } from 'utils/time';
import PatientTaskTable from '../PatientTaskTable';
import style from './EngagementPlans.scss';

const mapStateToProps = (state: ReduxState) => ({
  authPerson: getAuthPersonSelector(state),
  updatingEngagementPlan: getUpdatingEngagementPlanSelector(state),
  activeOrganizationId: getActiveOrganizationIdSelector(state),
  isDTCOrganizationCaregiver: isDTCOrganizationCaregiverSelector(state)
});

const mapDispatchToProps = {
  cancelEngagementPlan: cancelEngagementPlanThunk,
  clearUpdatingEngagementPlan: clearUpdatingEngagementPlanAction
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface EngagementPlansProps extends PropsFromRedux {
  patientId: string;
  patientEngagementPlans: PatientEngagementPlan[];
  onRefreshEngagementPlans?: () => void;
  hasDeviceSetup?: boolean;
}

enum ENGAGEMENT_PLAN_TABS {
  ACTIVE = 'Active',
  PAST = 'Past'
}

const EngagementPlans: React.FC<EngagementPlansProps> = ({
  authPerson,
  patientId,
  patientEngagementPlans,
  cancelEngagementPlan,
  onRefreshEngagementPlans,
  hasDeviceSetup = true,
  updatingEngagementPlan,
  clearUpdatingEngagementPlan,
  activeOrganizationId,
  isDTCOrganizationCaregiver
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [updatePlanDrawerOpen, setUpdatePlanDrawerOpen] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState<{
    engagementPlanId: string;
    patientEngagementPlanId?: string;
    title: string;
    description?: string;
    startDate: string;
    status: string;
    imageSource: string;
    durationDays: number;
    currentDay: number;
    isGlobal: boolean;
  } | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [planToCancel, setPlanToCancel] = useState<string | null>(null);
  const organizationId = activeOrganizationId;

  const activePlans = useMemo(() => (patientEngagementPlans || []).filter((plan) => plan.status === 'ACTIVE'), [patientEngagementPlans]);

  const pastPlans = useMemo(() => (patientEngagementPlans || []).filter((plan) => plan.status !== 'ACTIVE'), [patientEngagementPlans]);

  const handleAddEngagementPlan = () => {
    if (hasDeviceSetup) {
      setDrawerOpen(true);
    }
  };

  const handleCloseSelectionDrawer = () => {
    setDrawerOpen(false);
  };

  const handleCloseUpdatePlanDrawer = () => {
    setUpdatePlanDrawerOpen(false);
    clearUpdatingEngagementPlan();
  };

  const handleCloseDetailsDrawer = () => {
    setDetailsDrawerOpen(false);
    setSelectedPlanDetails(null);
  };

  const handleDetailsClick = (planId: string) => {
    const plan = patientEngagementPlans.find((p) => p.id === planId);
    if (plan) {
      // Calculate current day
      const calculateCurrentDay = (startDateString?: string) => {
        if (!startDateString) return 1;
        const startDate = parseDateStringIgnoreTimezone(startDateString);
        if (!startDate) return 1;
        const today = new Date();
        const timeDiff = today.getTime() - startDate.getTime();
        const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;
        return Math.max(1, daysDiff);
      };

      setSelectedPlanDetails({
        engagementPlanId: plan.engagementPlanId,
        patientEngagementPlanId: plan.id,
        title: plan.title || 'Untitled Plan',
        description: plan.description || '',
        startDate: plan.startDate || '',
        status: plan.status || '',
        durationDays: plan.durationDays || 0,
        currentDay: calculateCurrentDay(plan.startDate),
        imageSource: plan.imageSource || '',
        isGlobal: !plan?.organizationIds?.length
      });
      setDetailsDrawerOpen(true);
    }
  };

  const handleCancelClick = (planId: string) => {
    setPlanToCancel(planId);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (planToCancel) {
      try {
        await cancelEngagementPlan(planToCancel);
        // Refresh the engagement plans after successful cancellation
        if (onRefreshEngagementPlans) {
          onRefreshEngagementPlans();
        }
      } catch (error) {
        console.error('Failed to cancel engagement plan:', error);
      }
    }
    setCancelDialogOpen(false);
    setPlanToCancel(null);
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setPlanToCancel(null);
  };

  useEffect(() => {
    if (updatingEngagementPlan) {
      setUpdatePlanDrawerOpen(true);
    }
  }, [updatingEngagementPlan]);

  const renderAddPlanButton = () => (
    <Tooltip title={hasDeviceSetup ? '' : "The patient's device hasn't been set up yet"} followCursor placement="top">
      <span aria-label={hasDeviceSetup ? undefined : 'Button disabled: patient device setup required'}>
        <Button variant="contained" onClick={handleAddEngagementPlan} startIcon={<AddIcon />} disabled={!hasDeviceSetup}>
          Add Plan
        </Button>
      </span>
    </Tooltip>
  );

  const renderEngagementPlanCards = (plans: typeof patientEngagementPlans) => (
    <div className={style.cardsContainer}>
      {(plans || []).map((plan) => (
        <EngagementPlanCard
          key={plan.id}
          title={plan.title || 'Untitled Plan'}
          backgroundImage={plan.imageSource}
          startDate={plan.startDate}
          adherencePercent={plan.adherencePercent}
          completedTaskCount={plan.completedTaskCount}
          totalTaskCount={plan.taskCount}
          durationDays={plan.durationDays}
          status={plan.status}
          planId={plan.id}
          onCancel={handleCancelClick}
          onDetails={handleDetailsClick}
          isGlobal={!plan.organizationIds?.length}
        />
      ))}
    </div>
  );

  const tabs = useMemo(
    () => [
      {
        label: `${ENGAGEMENT_PLAN_TABS.ACTIVE} (${activePlans.length})`,
        content: renderEngagementPlanCards(activePlans)
      },
      {
        label: `${ENGAGEMENT_PLAN_TABS.PAST} (${pastPlans.length})`,
        content: renderEngagementPlanCards(pastPlans)
      }
    ],
    [activePlans, pastPlans, hasDeviceSetup]
  );

  const hasEngagementPlans = (patientEngagementPlans || []).length > 0;

  return (
    <div className={style.container}>
      {!isDTCOrganizationCaregiver && (
        <>
          <div className={style.titleRow}>
            <Text variant="xl">Engagement Plans</Text>
            {renderAddPlanButton()}
          </div>

          {hasEngagementPlans ? (
            <div className={style.tabsContainer}>
              <div className={style.tabsRow}>
                <div className={style.tabs}>
                  {tabs.map((tab, index) => (
                    <button key={index} className={classNames(style.tab, activeTab === index ? style.active : {})} onClick={() => setActiveTab(index)}>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={style.tabPanelContainer}>{tabs[activeTab].content}</div>
            </div>
          ) : (
            <EmptyListArea
              title="No plans yet"
              description="Start by creating an engagement plan"
              icon={CopyIcon}
              isButtonVisible={false}
              isSmallVersion
              containerStyle={style.emptyPlansContainer}
            />
          )}
        </>
      )}

      <PatientTaskTable patientEngagementPlans={patientEngagementPlans} onRefreshData={onRefreshEngagementPlans} hasDeviceSetup={hasDeviceSetup} />

      <EngagementPlanSelectionDrawer
        open={drawerOpen}
        onClose={handleCloseSelectionDrawer}
        organizationId={organizationId}
        patientId={patientId}
        onRefreshData={onRefreshEngagementPlans}
      />

      <EngagementPlanUpdateDrawer
        plan={updatingEngagementPlan!}
        open={updatePlanDrawerOpen}
        onClose={handleCloseUpdatePlanDrawer}
        patientId={patientId}
        onRefreshData={onRefreshEngagementPlans}
      />

      <ConfirmationDialogV2
        open={cancelDialogOpen}
        icon={WarningCircleIcon}
        title="Are you sure you want to cancel?"
        description="This cannot be undone."
        proceedEvent={handleCancelConfirm}
        proceedEventText="Cancel Plan"
        closeEvent={handleCancelDialogClose}
        closeEventText="Keep Plan"
      />

      <EngagementPlanDetailsDrawer
        open={detailsDrawerOpen}
        onClose={handleCloseDetailsDrawer}
        engagementPlanId={selectedPlanDetails?.engagementPlanId}
        patientEngagementPlanId={selectedPlanDetails?.patientEngagementPlanId}
        title={selectedPlanDetails?.title}
        description={selectedPlanDetails?.description}
        backgroundImage={selectedPlanDetails?.imageSource}
        startDate={selectedPlanDetails?.startDate}
        status={selectedPlanDetails?.status}
        durationDays={selectedPlanDetails?.durationDays}
        currentDay={selectedPlanDetails?.currentDay}
        onRefreshData={onRefreshEngagementPlans}
        isGlobalPlan={selectedPlanDetails?.isGlobal}
      />
    </div>
  );
};

export default connector(EngagementPlans);
